import { useCallback, useEffect, useState } from 'react';
import { ApiError, agentTitle, type AgentRow, type Ambience, type Api, type Contact, type VoiceSetting, type Kit, type KitLine, type Statement, type StoredKit } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';
import { PreviewPanel } from './Preview';
import { AssistBox, CompliancePanel, GeneralPanel, type EditableSpec } from './AgentSettings';
import { VoicePanel } from './VoicePanel';
import { WebCallButton } from '../components/WebCall';
import { KnowledgePanel } from './KnowledgePanel';
import { ActionsPanel } from './ActionsPanel';
import { RoutingPanel } from './RoutingPanel';
import { ConversationPanel } from './ConversationPanel';
import { ScorecardPanel } from './Scorecard';
import { TemplatePanel } from './TemplatePanel';
import { ReadinessCard, ReadinessPanel } from './ReadinessPanel';
import { OfferingsPanel } from './OfferingsPanel';
import type { Offering } from '../api';

const licenseKey = (l: KitLine): string =>
  l.license.evidence.includes('judge:conversational') ? 'license.conversational' : `license.${l.license.status}`;

const Line = ({ label, line, path, onSave, t }: { label?: string; line: KitLine; path: string; onSave: (path: string, text: string) => Promise<string | null>; t: T }) => {
  // ADR-071: editing a kit line needs the Agents permission; without it the line is shown read-only.
  const can = useCan();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(line.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(line.text), [line.text]);
  const save = async () => {
    setBusy(true);
    const err = await onSave(path, text.trim());
    setBusy(false);
    setError(err);
    if (!err) setEditing(false);
  };
  return (
    <div className="kit-line">
      {label && <div className="kit-label">{label}</div>}
      {editing ? (
        <div className="kit-edit">
          <textarea value={text} rows={2} onChange={(e) => setText(e.target.value)} />
          {error && <div className="banner error small">{error}</div>}
          <div className="row">
            <button type="button" className="primary" disabled={busy || !text.trim()} onClick={() => void save()}>{busy ? t('saving') : t('save')}</button>
            <button type="button" className="secondary" disabled={busy} onClick={() => { setEditing(false); setText(line.text); setError(null); }}>{t('cancel')}</button>
          </div>
        </div>
      ) : (
        <div className="kit-text">
          <span>{line.text}</span>
          <span className="kit-actions">
            <span className={`badge ${line.license.status}`}>{t(licenseKey(line))}</span>
            {can(PERM.agents) && <button type="button" className="link" onClick={() => setEditing(true)}>{t('edit')}</button>}
          </span>
        </div>
      )}
    </div>
  );
};

const KitView = ({ kit, onSave, t }: { kit: Kit; onSave: (path: string, text: string) => Promise<string | null>; t: T }) => (
  <div className="kit">
    <section>
      <h2>{t('kit.angles')} <span className="count">{kit.angles.length}</span></h2>
      <p className="muted">{t('kit.angles.hint')}</p>
      <div className="cards">
        {kit.angles.map((a) => (
          <article key={a.id} className="card">
            <h3>{a.title}</h3>
            <Line t={t} label={t('kit.pitch')} line={a.pitch} path={`angles.${a.id}.pitch`} onSave={onSave} />
            <Line t={t} label={t('kit.proof')} line={a.proof} path={`angles.${a.id}.proof`} onSave={onSave} />
            <Line t={t} label={t('kit.question')} line={a.discoveryQuestion} path={`angles.${a.id}.discoveryQuestion`} onSave={onSave} />
          </article>
        ))}
      </div>
    </section>
    <section>
      <h2>{t('kit.objections')} <span className="count">{kit.objections.length}</span></h2>
      <div className="cards">
        {kit.objections.map((o) => (
          <article key={o.id} className="card">
            <h3>{o.trigger}</h3>
            {o.responses.map((r, i) => <Line key={i} t={t} line={r} path={`objections.${o.id}.responses.${i}`} onSave={onSave} />)}
            <Line t={t} label={t('kit.objection.back')} line={o.returnQuestion} path={`objections.${o.id}.returnQuestion`} onSave={onSave} />
          </article>
        ))}
      </div>
    </section>
    <section className="two-col">
      <div>
        <h2>{t('kit.discovery')}</h2>
        {kit.discoveryQuestions.map((q) => (
          <article key={q.factKey} className="card">
            <h3 className="mono">{q.factKey}</h3>
            {q.variants.map((v, i) => <Line key={i} t={t} line={v} path={`discovery.${q.factKey}.${i}`} onSave={onSave} />)}
          </article>
        ))}
      </div>
      <div>
        <h2>{t('kit.closings')}</h2>
        {kit.closings.map((c) => (
          <article key={c.id} className="card">
            <h3 className="mono">{c.move}</h3>
            {c.lines.map((l, i) => <Line key={i} t={t} line={l} path={`closings.${c.id}.${i}`} onSave={onSave} />)}
          </article>
        ))}
      </div>
    </section>
    {(kit.plain ?? []).length > 0 && (
      <section>
        <h2>{t('kit.plain')} <span className="count">{kit.plain!.length}</span></h2>
        <p className="muted">{t('kit.plain.hint')}</p>
        <article className="card">{kit.plain!.map((l, i) => <Line key={i} t={t} line={l} path={`plain.${i}`} onSave={onSave} />)}</article>
      </section>
    )}
    <section className="two-col">
      <div>
        <h2>{t('kit.bridges')}</h2>
        <article className="card">{kit.bridges.map((b, i) => <Line key={i} t={t} line={b} path={`bridges.${i}`} onSave={onSave} />)}</article>
      </div>
      <div>
        <h2>{t('kit.fallbacks')}</h2>
        <article className="card">
          {(['silence', 'notUnderstood', 'technical'] as const).map((k) => (
            <Line key={k} t={t} label={t(`kit.fallback.${k}`)} line={kit.fallbacks[k]} path={`fallbacks.${k}`} onSave={onSave} />
          ))}
        </article>
      </div>
    </section>
  </div>
);

export const AgentPage = ({ api, t, agentId, initialTab, focus, onBack, onOpenCall }: { api: Api; t: T; agentId: string; initialTab?: string | undefined; focus?: string | undefined; onBack: () => void; onOpenCall: (id: string) => void }) => {
  // ADR-071: editing, publishing, building and AI tests need Agents; real and web calls need Call.
  const can = useCan();
  const canEdit = can(PERM.agents);
  const canCall = can(PERM.call);
  const [agent, setAgent] = useState<AgentRow>();
  const [statements, setStatements] = useState<Statement[]>();
  const [humor, setHumor] = useState<'off' | 'light'>('light');
  const [ambience, setAmbience] = useState<Ambience>();
  const [kits, setKits] = useState<StoredKit[]>();
  const [job, setJob] = useState<'idle' | 'running'>('idle');
  const [error, setError] = useState<string>();
  type Tab = 'general' | 'readiness' | 'offerings' | 'voice' | 'conversation' | 'knowledge' | 'actions' | 'routing' | 'kit' | 'compliance' | 'test';
  const TABS: readonly Tab[] = ['general', 'readiness', 'offerings', 'voice', 'conversation', 'knowledge', 'actions', 'routing', 'kit', 'compliance', 'test'];
  const [tab, setTab] = useState<Tab>(TABS.includes(initialTab as Tab) ? (initialTab as Tab) : 'general');
  const [spec, setSpec] = useState<{ version: number; spec: EditableSpec }>();
  const [live, setLive] = useState<{ specVersion: number; draftDiffers: boolean } | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [published, setPublished] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, k] = await Promise.all([api.agent(agentId), api.kits(agentId)]);
      setAgent(a.agent);
      if (a.spec) setSpec(a.spec as { version: number; spec: EditableSpec });
      setLive(a.published ?? null);
      setStatements((a.spec?.spec.statements as Statement[] | undefined) ?? []);
      setHumor(((a.spec?.spec.style as { humor?: 'off' | 'light' } | undefined)?.humor) ?? 'light');
      setAmbience((a.spec?.spec.ambience as Ambience | undefined) ?? { bed: 'none', level: 0.12 });
      setKits(k.items);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    }
  }, [api, agentId]);
  useEffect(() => void load(), [load]);

  const build = async () => {
    setError(undefined);
    setJob('running');
    try {
      const { jobId } = await api.buildKits(agentId);
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        const j = await api.job(jobId);
        if (j.status === 'done') break;
        if (j.status === 'failed') throw new ApiError(j.error?.code ?? 'generic', 500);
      }
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setJob('idle');
    }
  };

  const publish = async () => {
    setError(undefined);
    setPublishing(true);
    try {
      const r = await api.publish(agentId);
      setPublished(r.packageId);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setPublishing(false);
    }
  };

  const onSave = async (language: string, path: string, text: string): Promise<string | null> => {
    try {
      await api.editLine(agentId, language, path, text);
      await load();
      return null;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'KIT_LINE_UNLICENSED') {
        const reason = String(e.detail.reason ?? '');
        return `${t(`reason.${reason}`)} ${t('error.KIT_LINE_UNLICENSED')}`;
      }
      if (e instanceof ApiError && e.code === 'PERMISSION_DENIED') return t('error.PERMISSION_DENIED');
      return t('error.generic');
    }
  };

  const errText = (code: string) => (t(`error.${code}`) === `error.${code}` ? `${t('agent.kit.failed')} (${code})` : t(`error.${code}`));
  const current = kits?.[0];
  const report = current?.report as { generated?: number; repaired?: unknown[]; dropped?: unknown[]; editedPath?: string } | undefined;

  const status: 'live' | 'changed' | 'draft' = !agent?.publishedPackageId ? 'draft' : live?.draftDiffers ? 'changed' : 'live';
  return (
    <section>
      <button type="button" className="link back" onClick={onBack}>← {t('agent.back.short')}</button>
      <header className="page-head">
        <div>
          <div className="head-title">
            <h1>{agent ? agentTitle(agent) : '…'}</h1>
            {agent && <span className={`status ${status}`}>{t(`agent.status.${status}`)}</span>}
          </div>
          {agent && <AgentLabel api={api} t={t} agent={agent} onSaved={(label) => setAgent({ ...agent, label })} />}
          {agent && (
            <div className="head-meta">
              <span>{t(`direction.${agent.directionDefault}`)}</span>
              {live && <><span>·</span><span>{t('agent.liveVersion', { v: live.specVersion })}</span></>}
              {spec && status === 'changed' && <><span>·</span><span>{t('agent.draftVersion', { v: spec.version })}</span></>}
            </div>
          )}
        </div>
        <div className="head-actions">
          {agent?.publishedPackageId && canCall && <CallButton api={api} t={t} agentId={agentId} onOpenCall={onOpenCall} />}
          {current && (
            <button type="button" className="primary" disabled={publishing || job === 'running' || status === 'live' || !canEdit} onClick={() => void publish()} title={canEdit ? t('agent.publish.hint') : t('error.PERMISSION_DENIED')}>
              {publishing ? t('agent.publish.running') : status === 'live' ? t('agent.publish.upToDate') : t('agent.publish')}
            </button>
          )}
        </div>
      </header>
      {error && <div className="banner error">{errText(error)}</div>}
      {published && <div className="banner ok">{t('agent.publish.done', { id: published.slice(0, 8) })}</div>}
      <div className="tabs">
        {TABS.map((x) => (
          <button key={x} type="button" className={`tab ${tab === x ? 'active' : ''}`} onClick={() => setTab(x)}>{t(`agent.tab.${x}`)}</button>
        ))}
      </div>
      {tab === 'general' && spec && (
        <>
          <ReadOnlyNote t={t} need={PERM.agents} />
          <ReadinessCard key={`rc${formKey}`} api={api} t={t} agentId={agentId} onOpen={() => setTab('readiness')} />
          <TemplatePanel api={api} t={t} agentId={agentId} onChanged={() => void load().then(() => setFormKey((k) => k + 1))} />
          {canEdit && <AssistBox api={api} t={t} agentId={agentId} onSaved={() => void load().then(() => setFormKey((k) => k + 1))} />}
          <GeneralPanel key={`g${formKey}`} api={api} t={t} agentId={agentId} spec={spec.spec} onSaved={() => void load()} />
        </>
      )}
      {/* A question opened from a link starts the AI assistant at once (an Agents action), so only an editor gets it opened.
          The key remounts the panel when the permission answer arrives after a fresh load, so a question it already opened is dropped. */}
      {tab === 'readiness' && <ReadinessPanel key={canEdit ? 'rp-edit' : 'rp-view'} api={api} t={t} agentId={agentId} focus={canEdit ? focus : undefined} onGoTab={(x) => setTab(x)} onSaved={() => void load().then(() => setFormKey((k) => k + 1))} />}
      {tab === 'offerings' && spec && <OfferingsPanel key={`o${formKey}`} api={api} t={t} agentId={agentId} initial={(spec.spec.offerings as Offering[] | undefined) ?? []} onSaved={() => void load()} />}
      {tab === 'voice' && spec && (
        <VoicePanel key={`v${formKey}`} api={api} t={t} agentId={agentId} initial={(spec.spec.voice as VoiceSetting | undefined) ?? null} ambience={ambience ?? { bed: 'none', level: 0.12 }} onSaved={() => void load()} />
      )}
      {tab === 'knowledge' && agent && statements && (
        <KnowledgePanel key={`k${formKey}`} api={api} t={t} agentId={agentId} initial={statements} needsSource={agent.directionDefault !== 'inbound'} />
      )}
      {tab === 'conversation' && spec && <ConversationPanel key={`cv${formKey}`} api={api} t={t} agentId={agentId} spec={spec.spec} onSaved={() => void load()} />}
      {tab === 'actions' && spec && <ActionsPanel key={`a${formKey}`} api={api} t={t} agentId={agentId} spec={spec.spec} onSaved={() => void load()} />}
      {tab === 'routing' && spec && <RoutingPanel key={`r${formKey}`} api={api} t={t} agentId={agentId} spec={spec.spec} onSaved={() => void load()} />}
      {tab === 'compliance' && spec && <CompliancePanel key={`c${formKey}`} api={api} t={t} agentId={agentId} spec={spec.spec} onSaved={() => void load()} />}
      {tab === 'test' && (current ? (
        <>
          {agent?.publishedPackageId && canCall && (
            <>
              <h2>{t('webcall.section')}</h2>
              <p className="hint">{t('room.mode.me.hint')}</p>
              <WebCallButton inline api={api} t={t} agentId={agentId} agentName={agentTitle(agent)} onOpenCall={onOpenCall} />
              {canEdit && <h2 className="section-gap">{t('webcall.section.text')}</h2>}
            </>
          )}
          {canEdit && <PreviewPanel api={api} t={t} agentId={agentId} />}
          <h2 className="section-gap">{t('agent.tab.tests')}</h2>
          <ScorecardPanel api={api} t={t} agentId={agentId} />
        </>
      ) : <div className="empty"><p>{t('preview.needKit')}</p></div>)}
      {tab === 'kit' && kits && <ReadOnlyNote t={t} need={PERM.agents} />}
      {tab === 'kit' && kits && (
        <div className="kit-meta">
          <div>
            {current ? <span>{t('agent.kit.version', { v: current.kitVersion, lang: current.language })}</span> : <span className="muted">{t('agent.kit.none')}</span>}
            {report?.generated !== undefined && (
              <div className="hint">{t('agent.kit.report', { generated: report.generated, repaired: report.repaired?.length ?? 0, dropped: report.dropped?.length ?? 0 })}</div>
            )}
          </div>
          {canEdit && (
            <button type="button" className="secondary" disabled={job === 'running'} onClick={() => void build()}>
              {job === 'running' ? t('agent.kit.building') : current ? t('agent.kit.rebuild') : t('agent.kit.build')}
            </button>
          )}
        </div>
      )}
      {tab === 'kit' && current && <KitView kit={current.kit} t={t} onSave={(p, text) => onSave(current.language, p, text)} />}
    </section>
  );
};

/** ADR-062: the panel name — what the business calls the agent in its own panel; never said on a call. */
const AgentLabel = ({ api, t, agent, onSaved }: { api: Api; t: T; agent: AgentRow; onSaved: (label: string | null) => void }) => {
  // ADR-071: renaming needs the Agents permission; without it only the name line is shown.
  const can = useCan();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(agent.label ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.setAgentLabel(agent.id, value);
      onSaved(r.label);
      setEditing(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };
  if (!editing) {
    return (
      <p className="hint">
        {agent.label ? t('agent.label.speaking', { name: agent.name }) : t('agent.label.none')}{' '}
        {can(PERM.agents) && <button type="button" className="link" onClick={() => { setValue(agent.label ?? ''); setEditing(true); }}>{agent.label ? t('agent.label.edit') : t('agent.label.add')}</button>}
      </p>
    );
  }
  return (
    <div className="inline-form" style={{ marginTop: 6 }}>
      <input autoFocus maxLength={120} value={value} placeholder={t('agent.label.placeholder')} aria-label={t('agent.label.title')} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} />
      <button type="button" className="primary" disabled={busy} onClick={() => void save()}>{busy ? t('settings.saving') : t('settings.save')}</button>
      <button type="button" className="secondary" disabled={busy} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
      <span className="hint">{t('agent.label.hint')}</span>
      {error && <span className="banner error small">{error === 'PERMISSION_DENIED' ? t('error.PERMISSION_DENIED') : t('error.generic')}</span>}
    </div>
  );
};

/** Starts a real phone call with the published agent: number → confirm (it costs call minutes) → dialled. */
const CallButton = ({ api, t, agentId, onOpenCall }: { api: Api; t: T; agentId: string; onOpenCall: (id: string) => void }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [callId, setCallId] = useState<string>();
  // ADR-058: pick the number from the contacts (search by name, number, title or company).
  const [matches, setMatches] = useState<Contact[]>([]);
  const [picked, setPicked] = useState<Contact | null>(null);
  useEffect(() => {
    if (!open || confirming) return;
    const id = setTimeout(() => {
      api.contacts(picked ? '' : phone.trim(), 8).then((r) => setMatches(picked ? [] : r.contacts)).catch(() => setMatches([]));
    }, 200);
    return () => clearTimeout(id);
  }, [api, open, confirming, phone, picked]);

  const reset = () => {
    setOpen(false);
    setConfirming(false);
    setError(undefined);
  };
  const dial = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.startCall(agentId, phone.trim());
      setCallId(r.callId);
      setConfirming(false);
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <>
        <button type="button" className="secondary" onClick={() => { setOpen(true); setCallId(undefined); }}>☏ {t('call.start')}</button>
        {callId && (
          <div className="call-pop banner ok">
            {t('call.started')} <button type="button" className="link" onClick={() => onOpenCall(callId)}>{t('call.openCall')}</button>
          </div>
        )}
      </>
    );
  }
  return (
    <div className="call-pop card">
      {!confirming && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (phone.trim()) setConfirming(true);
          }}
        >
          <input type="text" autoFocus value={phone} placeholder={t('call.placeholder')} aria-label={t('call.placeholder')} onChange={(e) => { setPhone(e.target.value); setPicked(null); }} />
          <button type="submit" className="primary" disabled={!phone.trim()}>{t('call.next')}</button>
          <button type="button" className="secondary" onClick={reset}>{t('common.cancel')}</button>
        </form>
      )}
      {!confirming && picked && <p className="hint">{t('call.picked', { name: picked.name || picked.phone })}</p>}
      {!confirming && !picked && matches.length > 0 && (
        <div className="call-contacts">
          <p className="hint">{t('call.fromContacts')}</p>
          {matches.map((c) => {
            const f = c.fields as Record<string, unknown>;
            const sub = [f.title, f.company].filter((x) => typeof x === 'string' && x).join(' · ');
            return (
              <button key={c.id} type="button" className="list-row clickable" onClick={() => { setPhone(c.phone); setPicked(c); }}>
                <span className="grow"><span className="title">{c.name || c.phone}</span>{sub && <span className="sub"> — {sub}</span>}</span>
                <span className="mono small" dir="ltr">{c.phone}</span>
              </button>
            );
          })}
        </div>
      )}
      {confirming && (
        <div>
          <p>{t('call.confirm', { phone: picked?.name ? `${picked.name} (${phone.trim()})` : phone.trim() })}</p>
          <div className="row">
            <button type="button" className="primary" disabled={busy} onClick={() => void dial()}>{busy ? t('call.dialing') : t('call.yes')}</button>
            <button type="button" className="secondary" disabled={busy} onClick={() => setConfirming(false)}>{t('common.back')}</button>
          </div>
        </div>
      )}
      {error && <div className="banner error small">{t(`error.${error}`) === `error.${error}` ? `${t('error.generic')} (${error})` : t(`error.${error}`)}</div>}
    </div>
  );
};
